import React, { useEffect, useMemo, useRef, useState } from 'react';
import { uploadsEnabled, createJob, uploadFiles, createFolder } from '../../services/storageService';
import PromptGeneratorModal from '../PromptGeneratorModal';

const stepList = [
  { key: 'load', label: 'Load Data', color: '#60a5fa' },
  { key: 'controls', label: 'Controls', color: '#a78bfa' },
  { key: 'model', label: 'Model', color: '#fbbf24' },
  { key: 'prompt', label: 'Prompt', color: '#34d399' },
  { key: 'run', label: 'Run', color: '#9ca3af' },
  { key: 'output', label: 'Output', color: '#60a5fa' }
];

const fieldLabel = { color: '#111111', fontSize: 12 };
const inputStyle = { padding: '6px 10px', background: '#0b1020', color: '#e5e7eb', border: '1px solid #1f2937', borderRadius: 6 };

const hexToRgba = (hex, alpha = 0.12) => {
  try {
    const h = String(hex || '').replace('#', '');
    const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
    const bigint = parseInt(full, 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  } catch { return 'rgba(0,0,0,0)'; }
};

const MiniBAIQL = ({ miniState, onMiniChange, onGenerateCode, onSwitchToPro, isPro = false }) => {
  const {
    selected,
    pathIn, pathInGlobal, batchType,
    pathLines, extLines, pathFile, extFile, pathDir, extDir, pathRef, extRef, pathWork, extWork,
      model, modelGlobal, modelTemp, modelTopP,
      fallbackModel,
    prompt, promptOptimize,
    outFile, pathOut, pathOutGlobal, outFmt, printFormat, xact,
    batchMode = 'EXECUTE',
    validateN = 1,
    preprocess = false,
    preprocessCsvHeader = 'header',
    preprocessCsvNames = '',
    // LIMIT controls
    limitEnabled = false,
    limitGlobal = false,
    limitValue = 1,
    // ERROR controls
    errorEnabled = false,
    errorMode = '',
    errorRetries = 3,
    // TIMEOUT controls
    timeoutEnabled = false,
    timeoutSeconds = '',
    timeoutMode = 'SKIP',
    // DEADLINE controls
    deadlineEnabled = false,
    deadlineMinutes = '',
    deadlineMode = 'PARTIAL',
    // CHECKPOINT controls
    checkpointEnabled = false,
    checkpointEvery = 10,
    checkpointTimeout = 300,
    checkpointMsgEnabled = false,
    checkpointMessage = 'Type your message here.',
    // PARALLEL control
    parallelEnabled = false,
    parallelN = 1,
    // TAG control
    tagEnabled = false,
    tagText = ''
  } = miniState || {};

  // State for Prompt Generator Modal
  const [showPromptGeneratorModal, setShowPromptGeneratorModal] = useState(false);
  // Model options loaded from /api/ai/models
  const [modelOptions, setModelOptions] = useState([]);

  // Handle prompt drop-in from Prompt Generator Modal
  const handlePromptDropIn = (promptText) => {
    // If modal has text, replace existing text; if no text, persist existing text
    if (promptText && promptText.trim()) {
      onMiniChange && onMiniChange({ prompt: promptText.trim() });
    }
    // If no text, existing text persists (no change)
    setShowPromptGeneratorModal(false);
  };

  // Helpers
  const pickDirectory = (onPicked) => {
    // Web directory hint only
    const input = document.createElement('input');
    input.type = 'file';
    input.setAttribute('webkitdirectory', '');
    input.onchange = () => {
      const files = input.files;
      if (files && files.length > 0) {
        const rel = files[0].webkitRelativePath || files[0].name || '';
        const parts = rel.split('/');
        const topFolder = parts.length > 0 ? parts[0] : rel;
        onPicked(topFolder);
      }
    };
    input.click();
  };

  const sanitizePath = (p) => {
    const s = p == null ? '' : String(p);
    const trimmed = s.trim().replace(/^"|"$/g, '');
    return trimmed.replace(/\//g, '\\');
  };

  const escapeWindowsPathForScript = (s) => {
    if (s == null) return '';
    const withoutQuotes = String(s).replace(/^"|"$/g, '');
    // Collapse any run of backslashes to a single backslash
    const single = withoutQuotes.replace(/\\+/g, '\\');
    // Then escape to exactly double backslashes for script
    return single.replace(/\\/g, '\\\\');
  };

  // No absolute path detection needed in web mode

  const joinPaths = (base, child) => {
    const b = sanitizePath(base);
    const c = sanitizePath(child);
    if (!b) return c;
    if (!c) return b;
    const sep = b.endsWith('\\') ? '' : '\\';
    return `${b}${sep}${c}`;
  };

  const toPattern = (loc, ext) => {
    const base = sanitizePath(loc);
    const rawExt = ext == null ? '' : String(ext);
    const safeExt = rawExt.trim() ? rawExt.trim().replace(/^\./, '') : '*';
    const baseEsc = escapeWindowsPathForScript(base);
    return `${baseEsc}\\\\*.${safeExt}`; // emit double backslash in script
  };

  const buildBatchStart = () => {
    const mode = String(batchMode || 'EXECUTE').toUpperCase();
    if (mode === 'VALIDATE') return 'BATCH_START VALIDATE';
    if (mode === 'QUIET') return 'BATCH_START QUIET';
    return 'BATCH_START EXECUTE';
  };

  const enabledByBatch = useMemo(() => {
    // Determine which inputs (3-7) are enabled
    const map = { lines: false, file: false, dir: false, ref: false, work: false };
    switch (batchType) {
      case 'Generate content from variables':
        map.lines = true;
        break;
      case 'Analyze files from a fixed prompt':
        map.dir = true; // corrected per spec
        break;
      case 'Analyze files from a variable prompts':
        map.file = true;
        map.work = true;
        break;
      case 'Analyze with a single reference':
        map.file = true;
        map.ref = true;
        map.work = true;
        break;
      case 'Analyze with a multiple references':
        map.file = true;
        map.ref = true;
        map.work = true;
        break;
      default:
        break;
    }
    return map;
  }, [batchType]);

  // Load model list dynamically (keeps dropdown in sync with backend)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch('/api/ai/models');
        if (!resp.ok) throw new Error(`Failed to load models (${resp.status})`);
        const data = await resp.json();
        const opts = [];
        (data?.providers || []).forEach(p => {
          const allowed = Array.isArray(p.allowedModels) ? p.allowedModels : [];
          allowed.forEach(m => { if (m) opts.push(m); });
          // include defaultModel if not already in allowed
          if (p?.defaultModel) opts.push(p.defaultModel);
        });
        const dedup = Array.from(new Set(opts.filter(Boolean))).sort();
        if (!cancelled) setModelOptions(dedup);
      } catch (err) {
        console.error('[MiniBAIQL] Failed to load models', err);
        if (!cancelled) setModelOptions([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Ensure dropdown keeps showing the currently selected model even if not in list
  const allModelsForSelect = useMemo(() => {
    const set = new Set(modelOptions);
    if (model) set.add(model);
    return Array.from(set);
  }, [modelOptions, model]);

  const generatedCode = useMemo(() => {
    const lines = [];

    // PATH_IN line (GLOBAL optional)
    if (pathIn) {
      const pi = sanitizePath(pathIn);
      lines.push(`${pathInGlobal ? 'GLOBAL ' : ''}PATH_IN "${pi}"`);
    }

    // Determine LOAD lines per enabled inputs with OCR blocks
    const ocrExts = new Set(['pdf','jpg','jpeg','png','tif','tiff']);
    const loadBlocks = [];
    const pushOcrBlock = (loadLine, ext) => {
      if (ext && ocrExts.has(String(ext).toLowerCase())) {
        loadBlocks.push('');
        loadBlocks.push(loadLine);
        loadBlocks.push('SET OCR_IF_EMPTY = 1');
        loadBlocks.push('SET OCR_LANGS = "en"');
        loadBlocks.push('');
      } else {
        loadBlocks.push(loadLine);
      }
    };

    if (enabledByBatch.lines && pathLines) {
      pushOcrBlock(`LOAD "${toPattern(pathLines, extLines)}" AS LINES`, extLines);
    }
    if (enabledByBatch.file && pathFile) {
      pushOcrBlock(`LOAD "${toPattern(pathFile, extFile)}" AS FILE`, extFile);
    }
    if (enabledByBatch.dir && pathDir) {
      pushOcrBlock(`LOAD "${toPattern(pathDir, extDir)}" AS DIR`, extDir);
    }
    if (enabledByBatch.ref && pathRef) {
      const refs = pathRef.split(';').map(s => s.trim()).filter(Boolean);
      refs.forEach(r => {
        pushOcrBlock(`LOAD "${toPattern(r, extRef)}" AS REF`, extRef);
      });
    }
    if (enabledByBatch.work && pathWork) {
      pushOcrBlock(`LOAD "${toPattern(pathWork, extWork)}" AS WORK`, extWork);
    }

    if (loadBlocks.length > 0) {
      lines.push(buildBatchStart());
      lines.push(...loadBlocks);
    }

    // Model line (supports GLOBAL; include TEMP/TOP_P only when values differ from defaults)
    if (model) {
      const prefix = modelGlobal ? 'GLOBAL ' : '';
      const numTemp = Number(modelTemp);
      const numTopP = Number(modelTopP);
      const hasTemp = isFinite(numTemp) && numTemp !== 0.7;
      const hasTopP = isFinite(numTopP) && numTopP !== 1.0;
      const parts = [`${prefix}MODEL "${model}"`];
      if (hasTemp) parts.push(`TEMP ${numTemp}`);
      if (hasTopP) parts.push(`TOP_P ${numTopP}`);
      lines.push(parts.join(' '));
    }

    // Subsequent steps (kept): prompt/run/output; END_BATCH is managed in End step.
    if (prompt) lines.push(`PROMPT "${prompt}"`);
    lines.push('RUN');

    const inferredPathOut = pathOut || (outFile ? (() => { const ix = Math.max(outFile.lastIndexOf('\\'), outFile.lastIndexOf('/')); return ix > 0 ? outFile.slice(0, ix) : ''; })() : '');
    const inferredOutFile = outFile
      ? outFile
      : ( (inferredPathOut) ? `{PATH_OUT}\\result.${outFmt === 'csv' ? 'csv' : (outFmt === 'json' ? 'json' : 'txt')}` : '');
    if (inferredOutFile) lines.push(`OUTPUT "${inferredOutFile}"${outFmt ? ` FORMAT ${outFmt}` : ''}`);

    return lines.join('\n');
  }, [pathIn, pathInGlobal, enabledByBatch, pathLines, extLines, pathFile, extFile, pathDir, extDir, pathRef, extRef, pathWork, extWork, model, prompt, outFile, outFmt, pathOut]);

  const [uploadingIn, setUploadingIn] = useState(false);
  const [creatingOut, setCreatingOut] = useState(false);
  const inputUploadRef = useRef(null);

  const triggerUploadIn = () => {
    try { if (inputUploadRef.current) inputUploadRef.current.value = null; } catch {}
    try { if (inputUploadRef.current) inputUploadRef.current.click(); } catch {}
  };

  const onFilesChosenForIn = async (e) => {
    try {
      const files = Array.from(e?.target?.files || []);
      if (!files.length) return;
      setUploadingIn(true);
      let jobId = undefined;
      try {
        const job = await createJob(undefined);
        jobId = job?.jobId;
      } catch {}
      const res = await uploadFiles({ jobId, files });
      const serverPath = (res && res.serverPath) ? String(res.serverPath) : '';
      if (serverPath) {
        onMiniChange && onMiniChange({ pathIn: serverPath });
        onGenerateCode && onGenerateCode({ type: 'TOAST', level: 'success', message: 'Uploaded files. PATH_IN updated.' });
      } else {
        onGenerateCode && onGenerateCode({ type: 'TOAST', level: 'warning', message: 'Upload succeeded but no path returned.' });
      }
    } catch (err) {
      onGenerateCode && onGenerateCode({ type: 'TOAST', level: 'error', message: 'Upload failed' });
    } finally {
      setUploadingIn(false);
    }
  };

  const handleCreateOutFolder = async () => {
    try {
      setCreatingOut(true);
      const res = await createFolder({ parentPath: null, jobId: null, name: null });
      const serverPath = (res && res.serverPath) ? String(res.serverPath) : '';
      if (serverPath) {
        onMiniChange && onMiniChange({ pathOut: serverPath });
        onGenerateCode && onGenerateCode({ type: 'TOAST', level: 'success', message: 'Created output folder. PATH_OUT updated.' });
      } else {
        onGenerateCode && onGenerateCode({ type: 'TOAST', level: 'warning', message: 'Folder created but no path returned.' });
      }
    } catch (err) {
      onGenerateCode && onGenerateCode({ type: 'TOAST', level: 'error', message: 'Create folder failed' });
    } finally {
      setCreatingOut(false);
    }
  };

  // Derive a soft tint for the controls panel based on the current step color
  const stepDef = stepList.find(s => s.key === selected);
  const cardTint = (stepDef && stepDef.color) ? hexToRgba(stepDef.color, 0.32) : undefined;

  return (
    <div style={{ display: 'flex', gap: 12, height: '72vh' }}>
      {/* Left: flow steps */}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 10, marginTop: 32 }}>
        {stepList.map((s, idx) => (
          <button
            key={s.key}
            onClick={() => { onMiniChange && onMiniChange({ selected: s.key }); }}
            className="docs-tab"
            style={{
              display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderRadius: 8,
              background: selected === s.key ? '#111827' : '#0b1020',
              border: '1px solid #1f2937',
              textAlign: 'left'
            }}
          >
            <div style={{ width: 28, height: 28, borderRadius: 6, background: s.color }} />
            <div style={{ display: 'grid' }}>
              <div style={{ color: '#e5e7eb', fontWeight: 700 }}>{s.label}</div>
              <div style={{ color: '#9ca3af', fontSize: 12 }}>Step {idx + 1}</div>
            </div>
          </button>
        ))}
      </div>

      {/* Right: configuration form */}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <div className="dashboard-card" style={{ flex: 1, height: '100%', overflowY: 'auto' }}>
          <div style={{ padding: 12, borderBottom: '1px solid #1f2937', color: '#ffffff', display: 'flex', alignItems: 'center', gap: 12, background: '#0b1020' }}>
            <div style={{ fontWeight: 700 }}>Mini‑BAIQL (Basic)</div>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
              <button
                className="docs-tab"
                disabled={selected === 'run'}
                title={selected === 'run' ? 'Insert is disabled for RUN' : undefined}
                onClick={() => {
                  if (selected === 'prompt') {
                    onGenerateCode && onGenerateCode({ type: 'PROMPT', rawPromptText: prompt, optimize: !!promptOptimize });
                  } else if (selected === 'output') {
                    onGenerateCode && onGenerateCode({ type: 'OUTPUT', pathOut: sanitizePath(pathOut), pathOutGlobal: !!pathOutGlobal, dest: sanitizePath(outFile), fileType: (outFmt||'').trim(), printFormat: (printFormat||'').trim(), xact: !!xact });
                  } else if (selected === 'controls') {
                    // Validate TIMEOUT before emitting any payloads
                    if (timeoutEnabled) {
                      const secs = parseInt(String(timeoutSeconds || '').replace(/[^0-9]/g, ''), 10);
                      if (!isFinite(secs) || secs < 1 || secs > 999) {
                        onGenerateCode && onGenerateCode({ type: 'TOAST', level: 'warning', message: 'Enter TIMEOUT seconds between 1 and 999.' });
                        return;
                      }
                    }
                    // Validate DEADLINE
                    if (deadlineEnabled) {
                      const mins = parseInt(String(deadlineMinutes || '').replace(/[^0-9]/g, ''), 10);
                      if (!isFinite(mins) || mins < 1 || mins > 999) {
                        onGenerateCode && onGenerateCode({ type: 'TOAST', level: 'warning', message: 'Enter DEADLINE minutes between 1 and 999.' });
                        return;
                      }
                    }
                    // Emit LIMIT and ERROR/TIMEOUT payloads; editor will merge surgically
                    const valNum = Math.max(1, parseInt(limitValue, 10) || 1);
                    onGenerateCode && onGenerateCode({
                      type: 'LIMIT',
                      enabled: !!limitEnabled,
                      global: !!limitGlobal,
                      value: valNum
                    });
                    const retries = (() => {
                      const n = parseInt(errorRetries, 10);
                      if (isNaN(n)) return 3;
                      return Math.max(1, Math.min(n, 3));
                    })();
                    onGenerateCode && onGenerateCode({
                      type: 'ERROR',
                      enabled: !!errorEnabled,
                      mode: (errorMode ? String(errorMode).toUpperCase() : 'RETRY'),
                      retries
                    });
                    if (timeoutEnabled) {
                      const secs = Math.max(1, Math.min(999, parseInt(String(timeoutSeconds || '').replace(/[^0-9]/g, ''), 10) || 60));
                      const mode = (timeoutMode ? String(timeoutMode).toUpperCase() : 'SKIP');
                      onGenerateCode && onGenerateCode({
                        type: 'TIMEOUT',
                        enabled: true,
                        seconds: secs,
                        mode
                      });
                    } else {
                      onGenerateCode && onGenerateCode({
                        type: 'TIMEOUT',
                        enabled: false
                      });
                    }
                    if (deadlineEnabled) {
                      const mins = Math.max(1, Math.min(999, parseInt(String(deadlineMinutes || '').replace(/[^0-9]/g, ''), 10) || 60));
                      const dmode = (deadlineMode ? String(deadlineMode).toUpperCase() : 'PARTIAL');
                      onGenerateCode && onGenerateCode({
                        type: 'DEADLINE',
                        enabled: true,
                        minutes: mins,
                        mode: dmode
                      });
                    } else {
                      onGenerateCode && onGenerateCode({
                        type: 'DEADLINE',
                        enabled: false
                      });
                    }
                    // CHECKPOINT validation
                    if (checkpointEnabled) {
                      const ev = Math.max(2, Math.min(100, parseInt(String(checkpointEvery||'').replace(/[^0-9]/g, ''), 10) || 10));
                      const to = Math.max(30, Math.min(1800, parseInt(String(checkpointTimeout||'').replace(/[^0-9]/g, ''), 10) || 300));
                      if (ev < 2 || ev > 100) { onGenerateCode && onGenerateCode({ type:'TOAST', level:'warning', message:'CHECKPOINT EVERY must be 2–100.' }); return; }
                      if (to < 30 || to > 1800) { onGenerateCode && onGenerateCode({ type:'TOAST', level:'warning', message:'CHECKPOINT TIMEOUT must be 30–1800.' }); return; }
                      const msg = checkpointMsgEnabled ? (String(checkpointMessage||'').trim() || 'Type your message here.') : undefined;
                      onGenerateCode && onGenerateCode({
                        type:'CHECKPOINT',
                        enabled:true,
                        every: ev,
                        seconds: to,
                        message: msg
                      });
                    } else {
                      onGenerateCode && onGenerateCode({ type:'CHECKPOINT', enabled:false });
                    }
                    // PARALLEL
                    if (parallelEnabled) {
                      const pn = Math.max(1, Math.min(5, parseInt(String(parallelN || '').replace(/[^0-9]/g, ''), 10) || 1));
                      if (!isFinite(pn) || pn < 1 || pn > 5) { onGenerateCode && onGenerateCode({ type:'TOAST', level:'warning', message:'PARALLEL must be between 1 and 5.' }); return; }
                      onGenerateCode && onGenerateCode({ type:'PARALLEL', enabled:true, n: pn });
                    } else {
                      onGenerateCode && onGenerateCode({ type:'PARALLEL', enabled:false });
                    }
                    // TAG
                    if (tagEnabled) {
                      const raw = String(tagText || '');
                      const lines = raw.split(/\r?\n/).map(s => s.trim()).filter(Boolean);
                      const valid = [];
                      const valueOk = (rhs) => {
                        const t = rhs.trim();
                        return (/^\{[^}]+\}$/.test(t) || /^"(?:[^"\\]|\\.)*"$/.test(t));
                      };
                      for (const ln of lines) {
                        const eq = ln.indexOf('=');
                        if (eq <= 0) { onGenerateCode && onGenerateCode({ type:'TOAST', level:'warning', message:'Each TAG line must contain “=” with value in {...} or \"...\"' }); return; }
                        const left = ln.slice(0, eq).trim();
                        const right = ln.slice(1 + eq).trim();
                        if (!valueOk(right)) { onGenerateCode && onGenerateCode({ type:'TOAST', level:'warning', message:'TAG values must be in {...} or \"...\"' }); return; }
                        valid.push(`TAG ${left} = ${right}`);
                      }
                      if (valid.length > 0) {
                        onGenerateCode && onGenerateCode({ type:'TAG', enabled:true, lines: valid });
                      } else {
                        onGenerateCode && onGenerateCode({ type:'TAG', enabled:false });
                      }
                    } else {
                      onGenerateCode && onGenerateCode({ type:'TAG', enabled:false });
                    }
                  } else if (selected === 'load') {
                    // Validate that each enabled LOAD with a path has a selected file type
                    const violations = [];
                    if (enabledByBatch.lines && pathLines && !extLines) violations.push('LINES');
                    if (enabledByBatch.file && pathFile && !extFile) violations.push('FILE');
                    if (enabledByBatch.dir && pathDir && !extDir) violations.push('DIR');
                    if (enabledByBatch.ref && pathRef && !extRef) violations.push('REF');
                    if (enabledByBatch.work && pathWork && !extWork) violations.push('WORK');
                    if (violations.length > 0) {
                      onGenerateCode && onGenerateCode({ type: 'TOAST', level: 'error', message: 'You must choose a file type!' });
                      return;
                    }
                    // If preprocess is requested for DIR/WORK, emit PREPROCESS_LOADS payload
                    if (preprocess && ((enabledByBatch.dir && pathDir) || (enabledByBatch.work && pathWork))) {
                      const targets = [];
                      if (enabledByBatch.dir && pathDir) targets.push({ kind:'DIR', pattern: toPattern(pathDir, extDir), isCsv: String(extDir).toLowerCase()==='csv' });
                      if (enabledByBatch.work && pathWork) targets.push({ kind:'WORK', pattern: toPattern(pathWork, extWork), isCsv: String(extWork).toLowerCase()==='csv' });
                      const pi = sanitizePath(pathIn);
                      const pathInLine = pi ? `${pathInGlobal ? 'GLOBAL ' : ''}PATH_IN "${escapeWindowsPathForScript(pi)}"` : '';
                      onGenerateCode && onGenerateCode({
                        type: 'PREPROCESS_LOADS',
                        pathInLine,
                        targets,
                        csv: { mode: String(preprocessCsvHeader||'header'), names: String(preprocessCsvNames||'') }
                      });
                      return;
                    }

                    // Build LOAD/OCR blocks to merge without replacing the whole script
                    const ocrExts = new Set(['pdf','jpg','jpeg','png','tif','tiff']);
                    const loadLines = [];
                    const pushOcrBlock = (loadLine, ext) => {
                      if (ext && ocrExts.has(String(ext).toLowerCase())) {
                        loadLines.push('');
                        loadLines.push(loadLine);
                        loadLines.push('SET OCR_IF_EMPTY = 1');
                        loadLines.push('SET OCR_LANGS = "en"');
                        loadLines.push('');
                      } else {
                        loadLines.push(loadLine);
                      }
                    };
                    if (enabledByBatch.lines && pathLines) pushOcrBlock(`LOAD "${toPattern(pathLines, extLines)}" AS LINES`, extLines);
                    if (enabledByBatch.file && pathFile)   pushOcrBlock(`LOAD "${toPattern(pathFile, extFile)}" AS FILE`, extFile);
                    if (enabledByBatch.dir && pathDir)     pushOcrBlock(`LOAD "${toPattern(pathDir, extDir)}" AS DIR`, extDir);
                    if (enabledByBatch.ref && pathRef) {
                      const refs = pathRef.split(';').map(s => s.trim()).filter(Boolean);
                      refs.forEach(r => pushOcrBlock(`LOAD "${toPattern(r, extRef)}" AS REF`, extRef));
                    }
                    if (enabledByBatch.work && pathWork)   pushOcrBlock(`LOAD "${toPattern(pathWork, extWork)}" AS WORK`, extWork);

                    const pi = sanitizePath(pathIn);
                    const pathInLine = pi ? `${pathInGlobal ? 'GLOBAL ' : ''}PATH_IN "${escapeWindowsPathForScript(pi)}"` : '';
                    const batchStartLine = buildBatchStart();
                    onGenerateCode && onGenerateCode({ type: 'LOADS', pathInLine, loadLines, batchStartLine });
                  } else if (selected === 'model') {
                    onGenerateCode && onGenerateCode({ code: generatedCode, fallbackModel: (fallbackModel || '') });
                  } else {
                    onGenerateCode && onGenerateCode({ code: generatedCode });
                  }
                }}
              >
                Insert
              </button>
              <button className="docs-tab" title="Switch to Pro code view (F6 or Ctrl+Shift+M)" onClick={() => onSwitchToPro && onSwitchToPro()}>View Code</button>
            </div>
          </div>

          <div style={{ padding: '8px 8px 16px 16px', display: 'grid', gap: 14, marginTop: 0, background: cardTint }}>
            {selected === 'load' && (
              <div style={{ display: 'grid', gap: 14 }}>
                {/* PATH_IN row: 60% textbox, 10% caret, optional Upload, 30% GLOBAL */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: '60%' }}>
                    <div style={fieldLabel}>PATH_IN (folder)</div>
                    <input
                      style={{ ...inputStyle, width: '100%' }}
                      title="Type your base path (e.g., C:\\TestBAIQL\\data)"
                      value={pathIn}
                      onChange={(e) => onMiniChange && onMiniChange({ pathIn: e.target.value })}
                      placeholder="C:\\Path\\to\\work\\folder"
                    />
                  </div>
                  <div style={{ width: '10%', display: 'flex', alignItems: 'flex-end' }}>
                    <button className="docs-tab" style={{ width: '100%' }} onClick={() => pickDirectory((folder) => {
                      const base = sanitizePath(pathIn);
                      const next = base ? joinPaths(base, folder) : sanitizePath(folder);
                      onMiniChange && onMiniChange({ pathIn: next });
                    })}>▾</button>
                  </div>
                  {uploadsEnabled && (
                    <div>
                      <button className="docs-tab" disabled={uploadingIn} onClick={triggerUploadIn}>{uploadingIn ? 'Uploading…' : 'Upload'}</button>
                      <input ref={inputUploadRef} type="file" style={{ display:'none' }} multiple webkitdirectory="" onChange={onFilesChosenForIn} />
                    </div>
                  )}
                  <div style={{ width: '30%', display: 'flex', alignItems: 'flex-end', gap: 8 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input type="checkbox" checked={pathInGlobal} onChange={(e) => onMiniChange && onMiniChange({ pathInGlobal: e.target.checked })} />
                      <span style={{ color: '#111111' }}>GLOBAL</span>
                    </label>
                  </div>
                </div>
                {/* Inline tip removed; replaced by input title tooltip */}

                {/* Batch Type dropdown */}
                <div>
                  <div style={fieldLabel}>Batch Type</div>
                  <select style={inputStyle} value={batchType} onChange={(e)=>onMiniChange && onMiniChange({ batchType: e.target.value })}>
                    <option value="">Select…</option>
                    <option>Generate content from variables</option>
                    <option>Analyze files from a fixed prompt</option>
                    <option>Analyze files from a variable prompts</option>
                    <option>Analyze with a single reference</option>
                    <option>Analyze with a multiple references</option>
                  </select>
                </div>


                {/* Batch Options (mode) */}
                <div>
                  <div style={fieldLabel}>Batch Options</div>
                  <select
                    style={inputStyle}
                    value={batchMode}
                    onChange={(e) => onMiniChange && onMiniChange({ batchMode: e.target.value })}
                  >
                    <option value="EXECUTE">EXECUTE</option>
                    <option value="VALIDATE">VALIDATE</option>
                    <option value="QUIET">QUIET</option>
                  </select>
                </div>

                {/* Preprocess toggle for DIR/WORK */}
                {(enabledByBatch.dir || enabledByBatch.work) && (
                  <div style={{ display:'grid', gap:8 }}>
                    <label style={{ display:'flex', alignItems:'center', gap:8, color:'#000000' }}>
                      <input type="checkbox" checked={!!preprocess} onChange={(e)=>onMiniChange && onMiniChange({ preprocess: e.target.checked })} />
                      Preprocess (summarize) for multi‑CSV dirs
                    </label>
                    {preprocess && ((String(extDir).toLowerCase()==='csv') || (String(extWork).toLowerCase()==='csv')) && (
                      <div style={{ display:'grid', gap:6 }}>
                        <div style={{ color:'#000000' }}>CSV header?</div>
                        <div style={{ display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>
                          <label style={{ display:'flex', alignItems:'center', gap:6, color:'#000000' }}>
                            <input type="radio" name="pp-csv-hdr" checked={String(preprocessCsvHeader).toLowerCase()==='header'} onChange={()=>onMiniChange && onMiniChange({ preprocessCsvHeader: 'header' })} />
                            Has header
                          </label>
                          <label style={{ display:'flex', alignItems:'center', gap:6, color:'#00000' }}>
                            <input type="radio" name="pp-csv-hdr" checked={String(preprocessCsvHeader).toLowerCase()==='noheader'} onChange={()=>onMiniChange && onMiniChange({ preprocessCsvHeader: 'noheader' })} />
                            No header
                          </label>
                          {String(preprocessCsvHeader).toLowerCase()==='noheader' && (
                            <input style={{ ...inputStyle, width:'40%' }} placeholder="Column names (e.g., id,name,amount)" value={preprocessCsvNames} onChange={(e)=>onMiniChange && onMiniChange({ preprocessCsvNames: e.target.value })} />
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Inputs 3–7 */}
                {/* Helper to render radio line */}
                {(() => {
                  const RadioLine = ({ value, onChange, disabled }) => (
                    <div style={{ display: 'flex', gap: 12, flexWrap: 'nowrap' }}>
                      {['pdf','docx','xlsx','csv','json','zip'].map(ext => (
                        <label key={ext} style={{ display: 'flex', alignItems: 'center', gap: 6, color: disabled ? '#9ca3af' : '#e5e7eb' }}>
                          <input type="radio" name={`ext-${Math.random().toString(36).slice(2)}`} disabled={disabled} checked={value === ext} onChange={() => onChange(ext)} />
                          .{ext}
                        </label>
                      ))}
                    </div>
                  );

                  const InputBlock = ({ label, value, setValue, ext, setExt, enabled, isRef }) => (
                    <div style={{ display: 'grid', gap: 6, opacity: enabled ? 1 : 0.5 }}>
                      <div style={{ color: '#e5e7eb', fontWeight: 600 }}>{label}</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <input disabled={!enabled} style={{ ...inputStyle, width: '90%' }} value={value} onChange={(e)=>setValue(e.target.value)} placeholder={enabled ? 'C\\Path\\to\\folder or multiple;paths;for;REF' : ''} />
                        <button disabled={!enabled} className="docs-tab" style={{ width: '10%' }} onClick={() => pickDirectory((folder) => {
                          const base = sanitizePath(pathIn) || sanitizePath(value);
                          const full = base ? joinPaths(base, folder) : sanitizePath(folder);
                          const esc = escapeWindowsPathForScript(full);
                          const nextVal = isRef ? (value ? `${value};${esc}` : esc) : esc;
                          setValue(nextVal);
                        })}>▾</button>
                      </div>
                      <RadioLine value={ext} onChange={setExt} disabled={!enabled} />
                    </div>
                  );

                  return (
                    <details style={{ border:'1px solid #1f2937', borderRadius:8, background:'#0b1020' }}>
                      <summary style={{ cursor:'pointer', padding:'8px 12px', color:'#e5e7eb', fontWeight:600 }}>
                        LOAD Types - Open to Add
                      </summary>
                      <div style={{ padding:12, display: 'grid', gap: 16 }}>
                        <InputBlock label="LOAD Type 1 - LINES" value={pathLines} setValue={(v)=>onMiniChange && onMiniChange({ pathLines: v })} ext={extLines} setExt={(v)=>onMiniChange && onMiniChange({ extLines: v })} enabled={enabledByBatch.lines} isRef={false} />
                        <InputBlock label="LOAD Type 2 - FILE" value={pathFile} setValue={(v)=>onMiniChange && onMiniChange({ pathFile: v })} ext={extFile} setExt={(v)=>onMiniChange && onMiniChange({ extFile: v })} enabled={enabledByBatch.file} isRef={false} />
                        <InputBlock label="LOAD Type 3 - DIR" value={pathDir} setValue={(v)=>onMiniChange && onMiniChange({ pathDir: v })} ext={extDir} setExt={(v)=>onMiniChange && onMiniChange({ extDir: v })} enabled={enabledByBatch.dir} isRef={false} />
                        <InputBlock label="LOAD Type 4 - REF" value={pathRef} setValue={(v)=>onMiniChange && onMiniChange({ pathRef: v })} ext={extRef} setExt={(v)=>onMiniChange && onMiniChange({ extRef: v })} enabled={enabledByBatch.ref} isRef={true} />
                        <InputBlock label="LOAD Type 5 - WORK" value={pathWork} setValue={(v)=>onMiniChange && onMiniChange({ pathWork: v })} ext={extWork} setExt={(v)=>onMiniChange && onMiniChange({ extWork: v })} enabled={enabledByBatch.work} isRef={false} />
                      </div>
                    </details>
                  );
                })()}
              </div>
            )}

            {selected === 'model' && (
              <div style={{ display: 'grid', gap: 6, marginTop: 0 }}>
                {/* Row 1: Model dropdown (70%) + GLOBAL (30%) */}
                <div style={{ alignItems: 'flex-end', gap: 2 }}>
                  <div style={{ width: '70%' }}>
                    <div style={fieldLabel}>Model</div>
                    <select style={{ ...inputStyle, width: '100%' }} value={model} onChange={(e)=>onMiniChange && onMiniChange({ model: e.target.value })}>
                      <option value="">Select…</option>
                      {allModelsForSelect.map(m => (
                        <option key={m} value={m}>{m}</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ width: '30%', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <label style={{ display:'flex', alignItems:'center', gap:8 }}>
                      <input type="checkbox" checked={modelGlobal} onChange={(e)=>onMiniChange && onMiniChange({ modelGlobal: e.target.checked })} />
                      <span style={{ color:'#111111' }}>GLOBAL</span>
                    </label>
                  </div>
                </div>
                {/* Row 2: Temp and Top‑P spinners on same line */}
                <div style={{ display:'flex', alignItems:'center', gap: 6, marginTop: 2 }}>
                  <span style={{ color:'#111111', fontSize:12 }}>Temp</span>
                  <input type="number" min={0} max={2} step={0.1} style={{ ...inputStyle, width: 80 }} value={modelTemp} onChange={(e)=>onMiniChange && onMiniChange({ modelTemp: e.target.value })} />
                  <span style={{ color:'#111111', fontSize:12 }}>Top‑P</span>
                  <input type="number" min={0} max={1} step={0.05} style={{ ...inputStyle, width: 80 }} value={modelTopP} onChange={(e)=>onMiniChange && onMiniChange({ modelTopP: e.target.value })} />
                </div>
                {/* Row 3: Fallback Model dropdown */}
                <div style={{ display:'flex', justifyContent:'flex-start', marginTop: 12 }}>
                  <div style={{ width: '70%' }}>
                    <div style={fieldLabel}>Fallback Model</div>
                    <select
                      style={{ ...inputStyle, width: '100%' }}
                      disabled={!model}
                      title={!model ? 'Select a model first' : undefined}
                      value={fallbackModel || 'None'}
                      onChange={(e)=>onMiniChange && onMiniChange({ fallbackModel: e.target.value })}
                    >
                      <option value="None">None</option>
                      {allModelsForSelect
                        .filter(m => m !== model)
                        .map(m => (<option key={m} value={m}>{m}</option>))}
                    </select>
                  </div>
                </div>
              </div>
            )}

            {selected === 'prompt' && (
              <div style={{ display: 'grid', gap: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between' }}>
                  <div style={fieldLabel}>Prompt</div>
                  <button
                    className="docs-tab"
                    onClick={() => setShowPromptGeneratorModal(true)}
                    style={{ marginLeft: 'auto' }}
                  >
                    Open Prompt Generator
                  </button>
                </div>
                <textarea rows={4} style={inputStyle} value={prompt} onChange={(e) => onMiniChange && onMiniChange({ prompt: e.target.value })} placeholder="List 3 facts about {col1}" />
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: 6 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input type="checkbox" checked={!!promptOptimize} onChange={(e) => onMiniChange && onMiniChange({ promptOptimize: e.target.checked })} />
                    <span style={{ color: '#111111' }}>Optimize</span>
                  </label>
                </div>
              </div>
            )}

            {selected === 'controls' && (
              <div style={{ display:'grid', gap:8 }}>
                {/* LIMIT row - ~10px below header, left-aligned */}
                <div style={{ display:'flex', alignItems:'center', gap:12, marginTop: 10 }} title="Sets a number of AI transactions per minute.">
                  <label style={{ display:'flex', alignItems:'center', gap:8 }}>
                    <input
                      type="checkbox"
                      checked={!!limitEnabled}
                      onChange={(e)=>onMiniChange && onMiniChange({ limitEnabled: e.target.checked })}
                    />
                    <span style={{ color:'#111111' }}>LIMIT</span>
                  </label>
                  {(() => {
                    const maxCap = isPro ? 5 : 15; // Enterprise admin-configurable cap handled in editor; UI temp cap 15
                    const cur = Math.max(1, Math.min(parseInt(limitValue, 10) || 1, maxCap));
                    return (
                      <input
                        type="number"
                        min={1}
                        max={maxCap}
                        step={1}
                        style={{ ...inputStyle, width: 80 }}
                        value={cur}
                        onChange={(e)=>{
                          const raw = parseInt(e.target.value, 10);
                          const clamped = Math.max(1, Math.min(isNaN(raw) ? 1 : raw, maxCap));
                          onMiniChange && onMiniChange({ limitValue: String(clamped) });
                        }}
                      />
                    );
                  })()}
                  <label style={{ display:'flex', alignItems:'center', gap:8 }}>
                    <input
                      type="checkbox"
                      checked={!!limitGlobal}
                      onChange={(e)=>onMiniChange && onMiniChange({ limitGlobal: e.target.checked })}
                    />
                    <span style={{ color:'#111111' }}>GLOBAL</span>
                  </label>
                </div>

                {/* ERROR row - ~10px below LIMIT, left-aligned */}
                <div style={{ display:'flex', alignItems:'center', gap:12, marginTop: 10 }} title="An error in the AI transaction triggers the action.">
                  <label style={{ display:'flex', alignItems:'center', gap:8 }}>
                    <input
                      type="checkbox"
                      checked={!!errorEnabled}
                      onChange={(e)=>onMiniChange && onMiniChange({ errorEnabled: e.target.checked })}
                    />
                    <span style={{ color:'#111111' }}>ERROR</span>
                  </label>
                  <label style={{ display:'flex', alignItems:'center', gap:6, color: (!errorEnabled ? '#9ca3af' : '#111111') }}>
                    <input
                      type="radio"
                      name="error-mode"
                      disabled={!errorEnabled}
                      checked={String(errorMode).toUpperCase() === 'HALT'}
                      onChange={()=>onMiniChange && onMiniChange({ errorMode: 'HALT' })}
                    />
                    <span>HALT</span>
                  </label>
                  <label style={{ display:'flex', alignItems:'center', gap:6, color: (!errorEnabled ? '#9ca3af' : '#111111') }}>
                    <input
                      type="radio"
                      name="error-mode"
                      disabled={!errorEnabled}
                      checked={String(errorMode).toUpperCase() === 'RETRY'}
                      onChange={()=>onMiniChange && onMiniChange({ errorMode: 'RETRY' })}
                    />
                    <span>RETRY</span>
                  </label>
                  {(() => {
                    const cur = Math.max(1, Math.min(parseInt(errorRetries, 10) || 3, 3));
                    return (
                      <input
                        type="number"
                        min={1}
                        max={3}
                        step={1}
                        disabled={!errorEnabled || String(errorMode).toUpperCase() !== 'RETRY'}
                        style={{ ...inputStyle, width: 80 }}
                        value={cur}
                        onChange={(e)=>{
                          const raw = parseInt(e.target.value, 10);
                          const clamped = Math.max(1, Math.min(isNaN(raw) ? 3 : raw, 3));
                          onMiniChange && onMiniChange({ errorRetries: String(clamped) });
                        }}
                      />
                    );
                  })()}
                </div>

                {/* TIMEOUT row - ~10px below ERROR */}
                <div style={{ display:'flex', alignItems:'center', gap:12, marginTop: 10 }} title="If the AI response exceeds 'seconds', the action triggers.">
                  <label style={{ display:'flex', alignItems:'center', gap:8 }}>
                    <input
                      type="checkbox"
                      checked={!!timeoutEnabled}
                      onChange={(e)=>{
                        const next = e.target.checked;
                        if (next) {
                          const nextSecs = (String(timeoutSeconds||'').trim().length === 0) ? '60' : String(timeoutSeconds);
                          const nextMode = String(timeoutMode||'').trim().length === 0 ? 'SKIP' : timeoutMode;
                          onMiniChange && onMiniChange({ timeoutEnabled: true, timeoutSeconds: nextSecs, timeoutMode: nextMode });
                        } else {
                          onMiniChange && onMiniChange({ timeoutEnabled: false });
                        }
                      }}
                    />
                    <span style={{ color:'#111111' }}>TIMEOUT</span>
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={999}
                    step={1}
                    disabled={!timeoutEnabled}
                    style={{ ...inputStyle, width: 80 }}
                    value={String(timeoutSeconds || '')}
                    onChange={(e)=>{
                      const onlyDigits = String(e.target.value || '').replace(/[^0-9]/g, '');
                      let n = parseInt(onlyDigits, 10);
                      if (!isFinite(n)) { onMiniChange && onMiniChange({ timeoutSeconds: '' }); return; }
                      n = Math.max(1, Math.min(999, n));
                      onMiniChange && onMiniChange({ timeoutSeconds: String(n) });
                    }}
                  />
                  <label style={{ display:'flex', alignItems:'center', gap:6, color: (!timeoutEnabled ? '#9ca3af' : '#111111') }}>
                    <input
                      type="radio"
                      name="timeout-mode"
                      disabled={!timeoutEnabled}
                      checked={String(timeoutMode).toUpperCase() === 'SKIP'}
                      onChange={()=>onMiniChange && onMiniChange({ timeoutMode: 'SKIP' })}
                    />
                    <span>SKIP</span>
                  </label>
                  <label style={{ display:'flex', alignItems:'center', gap:6, color: (!timeoutEnabled ? '#9ca3af' : '#111111') }}>
                    <input
                      type="radio"
                      name="timeout-mode"
                      disabled={!timeoutEnabled}
                      checked={String(timeoutMode).toUpperCase() === 'HALT'}
                      onChange={()=>onMiniChange && onMiniChange({ timeoutMode: 'HALT' })}
                    />
                    <span>HALT</span>
                  </label>
                  <label style={{ display:'flex', alignItems:'center', gap:6, color: (!timeoutEnabled ? '#9ca3af' : '#111111') }}>
                    <input
                      type="radio"
                      name="timeout-mode"
                      disabled={!timeoutEnabled}
                      checked={String(timeoutMode).toUpperCase() === 'RETRY'}
                      onChange={()=>onMiniChange && onMiniChange({ timeoutMode: 'RETRY' })}
                    />
                    <span>RETRY</span>
                  </label>
                </div>

                {/* DEADLINE row - ~10px below TIMEOUT */}
                <div style={{ display:'flex', alignItems:'center', gap:12, marginTop: 10 }} title="If the AI batch doesn't complete in 'minutes', the action triggers.">
                  <label style={{ display:'flex', alignItems:'center', gap:8 }}>
                    <input
                      type="checkbox"
                      checked={!!deadlineEnabled}
                      onChange={(e)=>{
                        const next = e.target.checked;
                        if (next) {
                          const nextMins = (String(deadlineMinutes||'').trim().length === 0) ? '60' : String(deadlineMinutes);
                          const nextMode = String(deadlineMode||'').trim().length === 0 ? 'PARTIAL' : deadlineMode;
                          onMiniChange && onMiniChange({ deadlineEnabled: true, deadlineMinutes: nextMins, deadlineMode: nextMode });
                        } else {
                          onMiniChange && onMiniChange({ deadlineEnabled: false });
                        }
                      }}
                    />
                    <span style={{ color:'#111111' }}>DEADLINE</span>
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={999}
                    step={1}
                    disabled={!deadlineEnabled}
                    style={{ ...inputStyle, width: 80 }}
                    value={String(deadlineMinutes || '')}
                    onChange={(e)=>{
                      const onlyDigits = String(e.target.value || '').replace(/[^0-9]/g, '');
                      let n = parseInt(onlyDigits, 10);
                      if (!isFinite(n)) { onMiniChange && onMiniChange({ deadlineMinutes: '' }); return; }
                      n = Math.max(1, Math.min(999, n));
                      onMiniChange && onMiniChange({ deadlineMinutes: String(n) });
                    }}
                  />
                  <label style={{ display:'flex', alignItems:'center', gap:6, color: (!deadlineEnabled ? '#9ca3af' : '#111111') }}>
                    <input
                      type="radio"
                      name="deadline-mode"
                      disabled={!deadlineEnabled}
                      checked={String(deadlineMode).toUpperCase() === 'HALT'}
                      onChange={()=>onMiniChange && onMiniChange({ deadlineMode: 'HALT' })}
                    />
                    <span>HALT</span>
                  </label>
                  <label style={{ display:'flex', alignItems:'center', gap:6, color: (!deadlineEnabled ? '#9ca3af' : '#111111') }}>
                    <input
                      type="radio"
                      name="deadline-mode"
                      disabled={!deadlineEnabled}
                      checked={String(deadlineMode).toUpperCase() === 'PARTIAL'}
                      onChange={()=>onMiniChange && onMiniChange({ deadlineMode: 'PARTIAL' })}
                    />
                    <span>PARTIAL</span>
                  </label>
                </div>

                {/* CHECKPOINT rows - ~10px below DEADLINE */}
                <div style={{ display:'grid', gap:8, marginTop: 10 }} title="The AI batch will pause for review every 'n' transactions for 'seconds'.">
                  {/* Line 1: main checkbox */}
                  <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                    <label style={{ display:'flex', alignItems:'center', gap:8 }}>
                      <input
                        type="checkbox"
                        checked={!!checkpointEnabled}
                        onChange={(e)=>{
                          const next = e.target.checked;
                          if (next) {
                            onMiniChange && onMiniChange({
                              checkpointEnabled: true,
                              checkpointEvery: Number.isFinite(parseInt(String(checkpointEvery),10)) ? checkpointEvery : 10,
                              checkpointTimeout: Number.isFinite(parseInt(String(checkpointTimeout),10)) ? checkpointTimeout : 300
                            });
                          } else {
                            onMiniChange && onMiniChange({ checkpointEnabled: false });
                          }
                        }}
                      />
                      <span style={{ color:'#111111' }}>CHECKPOINT</span>
                    </label>
                  </div>
                  {/* Line 2: indented EVERY/TIMEOUT controls */}
                  <div style={{ display:'flex', alignItems:'center', gap:12, paddingLeft: 28 }}>
                    <span style={{ color:'#111111' }}>EVERY</span>
                    <input
                      type="number"
                      min={2}
                      max={100}
                      step={1}
                      disabled={!checkpointEnabled}
                      style={{ ...inputStyle, width: 80 }}
                      value={checkpointEvery}
                      onChange={(e)=>{
                        const n = parseInt(e.target.value, 10);
                        const clamped = Math.max(2, Math.min(100, isNaN(n) ? 10 : n));
                        onMiniChange && onMiniChange({ checkpointEvery: clamped });
                      }}
                    />
                    <span style={{ color:'#111111' }}>TIMEOUT</span>
                    <input
                      type="number"
                      min={30}
                      max={1800}
                      step={10}
                      disabled={!checkpointEnabled}
                      style={{ ...inputStyle, width: 100 }}
                      value={checkpointTimeout}
                      onChange={(e)=>{
                        const n = parseInt(e.target.value, 10);
                        const clamped = Math.max(30, Math.min(1800, isNaN(n) ? 300 : n));
                        onMiniChange && onMiniChange({ checkpointTimeout: clamped });
                      }}
                    />
                  </div>
                  {/* Line 3: indented message toggle */}
                  <div style={{ display:'flex', alignItems:'center', gap:12, paddingLeft: 28 }}>
                    <label style={{ display:'flex', alignItems:'center', gap:8 }}>
                      <input
                        type="checkbox"
                        checked={!!checkpointMsgEnabled}
                        disabled={!checkpointEnabled}
                        onChange={(e)=>onMiniChange && onMiniChange({ checkpointMsgEnabled: e.target.checked })}
                      />
                      <span style={{ color:'#111111' }}>MESSAGE</span>
                    </label>
                    <input
                      type="text"
                      disabled={!checkpointEnabled || !checkpointMsgEnabled}
                      style={{ ...inputStyle, width: '60%' }}
                      value={checkpointMessage}
                      onChange={(e)=>onMiniChange && onMiniChange({ checkpointMessage: e.target.value })}
                      placeholder="Type your message here."
                    />
                  </div>
                </div>

                {/* PARALLEL row - ~10px below CHECKPOINT */}
                <div style={{ display:'flex', alignItems:'center', gap:12, marginTop: 10 }} title="The AI batch can be sent to [n] transactions at a time.">
                  <label style={{ display:'flex', alignItems:'center', gap:8 }}>
                    <input
                      type="checkbox"
                      checked={!!parallelEnabled}
                      onChange={(e)=>{
                        const next = e.target.checked;
                        const defN = (parallelN == null || String(parallelN) === '') ? 1 : parallelN;
                        onMiniChange && onMiniChange({ parallelEnabled: next, parallelN: defN });
                      }}
                    />
                    <span style={{ color:'#111111' }}>PARALLEL</span>
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={5}
                    step={1}
                    disabled={!parallelEnabled}
                    style={{ ...inputStyle, width: 80 }}
                    value={String(parallelN ?? '1')}
                    onChange={(e)=>{
                      const raw = parseInt(e.target.value, 10);
                      const clamped = Math.max(1, Math.min(isNaN(raw) ? 1 : raw, 5));
                      onMiniChange && onMiniChange({ parallelN: String(clamped) });
                    }}
                  />
                </div>

                {/* TAG block - ~10px below PARALLEL */}
                <div style={{ display:'grid', gap:8, marginTop: 10 }} title="Set information variables to add to your output.">
                  {/* Line 1: TAG checkbox */}
                  <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                    <label style={{ display:'flex', alignItems:'center', gap:8 }}>
                      <input
                        type="checkbox"
                        checked={!!tagEnabled}
                        onChange={(e)=>{
                          const next = e.target.checked;
                          if (next) {
                            onMiniChange && onMiniChange({ tagEnabled: true, tagText: tagText || '' });
                          } else {
                            onMiniChange && onMiniChange({ tagEnabled: false });
                          }
                        }}
                      />
                      <span style={{ color:'#111111' }}>TAG</span>
                    </label>
                  </div>
                  {/* Line 2: indented textarea */}
                  <div style={{ paddingLeft: 28 }}>
                    <textarea
                      rows={3}
                      style={{ ...inputStyle, width: '35ch', resize: 'vertical', overflow: 'auto' }}
                      disabled={!tagEnabled}
                      value={tagText}
                      onChange={(e)=>onMiniChange && onMiniChange({ tagText: e.target.value })}
                      placeholder={`Enter one per line, e.g.:\n{customerId} = "12345"\n{region} = {regionName}`}
                    />
                  </div>
                </div>
              </div>
            )}

            {selected === 'run' && (
              <div style={{ display:'grid', gap:8 }}>
                <div style={{ color:'#000000' }}>RUN is a command that is required for every script to work. It is included by default.</div>
                <div style={{ color:'#000000' }}>RUN will iterate through loaded items and call the selected model.</div>
              </div>
            )}

            {selected === 'output' && (
              <div style={{ display: 'grid', gap: 12 }}>
                {/* PATH_OUT + GLOBAL */}
                <div style={{ display:'flex', alignItems:'flex-end', gap:8 }}>
                  <div style={{ flex:1 }}>
                    <div style={fieldLabel}>PATH_OUT (folder)</div>
                    <input style={{ ...inputStyle, width:'100%' }} value={pathOut} onChange={(e)=>{ const v = e.target.value; onMiniChange && onMiniChange({ pathOut: v }); }} placeholder="C:\\Path\\to\\AI\\output" />
                  </div>
                  {uploadsEnabled && (
                    <div>
                      <button className="docs-tab" disabled={creatingOut} onClick={handleCreateOutFolder}>{creatingOut ? 'Creating…' : 'Create'}</button>
                    </div>
                  )}
                  <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                    <label style={{ display:'flex', alignItems:'center', gap:8 }}>
                      <input type="checkbox" checked={!!pathOutGlobal} onChange={(e)=>{ const v = e.target.checked; onMiniChange && onMiniChange({ pathOutGlobal: v }); }} />
                      <span style={{ color:'#111111' }}>GLOBAL</span>
                    </label>
                  </div>
                </div>

                {/* Directory\\filename */}
                <div>
                  <div style={fieldLabel}>Directory\\filename</div>
                  <input style={inputStyle} value={outFile} onChange={(e)=>{ const v = e.target.value; onMiniChange && onMiniChange({ outFile: v }); }} placeholder="output\\loandoc_{#100#}" />
                </div>

                {/* File Type */}
                <div>
                  <div style={fieldLabel}>File Type</div>
                  <select style={inputStyle} value={outFmt} onChange={(e)=>{ const v = e.target.value; onMiniChange && onMiniChange({ outFmt: v }); }}>
                    <option value="">Select…</option>
                    <option value="txt">txt - Plain text</option>
                    <option value="csv">csv - Comma-separated values</option>
                    <option value="json">json - JSON format</option>
                    <option value="xlsx">xlsx - Excel spreadsheet</option>
                    <option value="docx">docx - Word document</option>
                  </select>
                </div>

                {/* Print format */}
                <div>
                  <div style={fieldLabel}>Print format</div>
                  <select style={inputStyle} value={printFormat} onChange={(e)=>{ const v = e.target.value; onMiniChange && onMiniChange({ printFormat: v }); }}>
                    <option value="">Select…</option>
                    <option value="MATCH">MATCH</option>
                    <option value="AGGREGATE">AGGREGATE</option>
                    <option value="SEPARATE">SEPARATE</option>
                    <option value="SCREEN">SCREEN</option>
                  </select>
                </div>

                {/* XACT */}
                <div>
                  <label style={{ display:'flex', alignItems:'center', gap:8 }}>
                    <input type="checkbox" checked={!!xact} onChange={(e)=>{ const v = e.target.checked; onMiniChange && onMiniChange({ xact: v }); }} />
                    <span style={{ color:'#111111' }}>Add Transaction Data</span>
                  </label>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>

      {/* Prompt Generator Modal */}
      <PromptGeneratorModal
        isOpen={showPromptGeneratorModal}
        onClose={() => setShowPromptGeneratorModal(false)}
        onDropIn={handlePromptDropIn}
      />
    </div>
  );
};

export default MiniBAIQL;


